import { Card, Checkbox, IconButton, Input, Text, VSpacer } from '@/components/DesignSystem';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EnergyBehaviorForm } from './energyValidation';

interface EnergyBehaviorCardProps {
  behavior: EnergyBehaviorForm,
  errorText?: string,
  index: number,
  // Once points are issued, only the behavior's text can change.
  isRuleLocked?: boolean,
  onChange: (behavior: EnergyBehaviorForm) => void,
  onRemove: () => void,
  testID: string,
}

export const EnergyBehaviorCard = ({
  behavior,
  errorText,
  index,
  isRuleLocked = false,
  onChange,
  onRemove,
  testID,
}: EnergyBehaviorCardProps) => {
  const { t } = useTranslation();
  const isInUse = behavior.completionCount > 0;
  const removeButton = (
    <span>
      <IconButton
        aria-label={t(($) => $.energy.tiers.removeBehavior, { number: index + 1 })}
        disabled={isInUse}
        onClick={onRemove}
        testID={`${testID}-remove`}
      >
        <DeleteOutline />
      </IconButton>
    </span>
  );

  return (
    <Card testID={testID}>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text category="title-small">
          {t(($) => $.energy.tiers.behaviorTitle, { number: index + 1 })}
        </Text>
        {!isRuleLocked && (isInUse ? (
          <Tooltip title={t(($) => $.energy.tiers.behaviorInUse)}>
            {removeButton}
          </Tooltip>
        ) : removeButton)}
      </Stack>
      <VSpacer size="4" />
      <Input
        error={!!errorText && !behavior.name.trim()}
        label={t(($) => $.energy.tiers.behaviorName)}
        onChangeText={(name) => onChange({ ...behavior, name })}
        placeholder={t(($) => $.energy.tiers.behaviorNamePlaceholder)}
        testID={`${testID}-name`}
        value={behavior.name}
      />
      <VSpacer size="4" />
      <Input
        error={!!errorText && !behavior.description.trim()}
        label={t(($) => $.energy.tiers.behaviorDescription)}
        onChangeText={(description) => onChange({ ...behavior, description })}
        testID={`${testID}-description`}
        value={behavior.description}
      />
      <VSpacer size="4" />
      <Input
        helperText={t(($) => $.energy.tiers.behaviorSkuHint)}
        label={t(($) => $.energy.tiers.behaviorSku)}
        onChangeText={(sku) => onChange({ ...behavior, sku })}
        placeholder={t(($) => $.energy.tiers.behaviorSkuPlaceholder)}
        testID={`${testID}-sku`}
        value={behavior.sku}
      />
      <VSpacer size="3" />
      <Checkbox
        checked={behavior.isRequiredForTier}
        disabled={isRuleLocked}
        onChangeChecked={(isRequiredForTier) => onChange({ ...behavior, isRequiredForTier })}
        testID={`${testID}-required`}
      >
        <Text category="body-medium">{t(($) => $.energy.tiers.requiredForTier)}</Text>
      </Checkbox>
      {!!errorText && (
        <>
          <VSpacer size="3" />
          <Text category="body-small" sx={{ color: 'error.main' }} testID={`${testID}-error`}>
            {errorText}
          </Text>
        </>
      )}
    </Card>
  );
};
